import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { Helmet } from 'react-helmet';
import Typography from '@material-ui/core/Typography';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import { ROUTE_TERMS_AND_CONDITIONS } from '@/constants/routes';
import { LOCATIONS_TERMS_CONDITIONS_PAGE } from '@/constants/locations';

// UTILITY
import { removeHTMLEntity } from '@/utility/common';
import {
  connectScripts,
  mainComposerScripts,
  manageClickListeners,
  removeCss,
  removeScripts,
  runComposer,
  wpComposerAssetsPath,
  wpComposerPublicPath,
  WPData,
} from '@/utility/wp';
import { formatDate } from '@/utility/date';

// STORE
import { AuthActions, WPBlogActions } from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import WPPage from '@/hoc/WPPage';

// COMPONENTS
import ReactHtml from '@/components/ReactHtml';
import Loader from '@/components/Loader';
import RouterBreadcrumbs from '@/components/RouterBreadcrumbs';

// STYLES
import { WPContent } from './styles';

const wpData = new WPData();

class TermsAndConditions extends WPPage {
  state = {
    mainCssLoaded: false,
  };

  componentDidMount() {
    this.getPage();

    window.prerendercloudReady = false;
  }

  componentDidUpdate(prevProps, prevState) {
    const { mainCssLoaded } = this.state;
    const { data, loading, isAuth } = this.props;
    const dataLength = Object.keys(data).length;

    if (prevState.mainCssLoaded !== mainCssLoaded && mainCssLoaded) {
      const externalLinks = document.querySelectorAll('a.wp-external-link');
      const authButtons = document.querySelectorAll('.auth-dialog button');

      manageClickListeners(externalLinks, 'add', this.handleExternalWPLink);
      manageClickListeners(authButtons, 'add', this.handleOpenAuthDialog);
    }

    if (prevProps.data !== data && dataLength) {
      if (data?.x_metadata?.vcvSourceAssetsFiles?.jsBundles) {
        connectScripts(data.x_metadata.vcvSourceAssetsFiles.jsBundles, wpData);
      }

      if (data?.x_metadata?.vcvSourceCssFileUrl) {
        this.connectCss(wpComposerAssetsPath + data.x_metadata.vcvSourceCssFileUrl);
      } else {
        this.setState({ mainCssLoaded: true });
      }
    }

    if (prevProps.loading && prevProps.loading !== loading && !dataLength) {
      this.setState({ mainCssLoaded: true });
    }

    if (prevProps.isAuth !== isAuth) {
      this.removeWPFromDOM();
      this.setState({ mainCssLoaded: false }, this.getPage);
    }
  }

  componentWillUnmount() {
    this.removeWPFromDOM();
  }

  getPage = () => {
    const { getWPPage } = this.props;

    getWPPage('terms-and-conditions');

    connectScripts(mainComposerScripts, wpData);
  };

  removeWPFromDOM = () => {
    const { data } = this.props;
    let scripts = mainComposerScripts;

    const externalLinks = document.querySelectorAll('a.wp-external-link');
    const authButtons = document.querySelectorAll('.auth-dialog button');

    manageClickListeners(externalLinks, 'remove', this.handleExternalWPLink);
    manageClickListeners(authButtons, 'remove', this.handleOpenAuthDialog);

    if (data?.x_metadata?.vcvSourceAssetsFiles?.jsBundles) {
      scripts = [...scripts, ...data.x_metadata.vcvSourceAssetsFiles.jsBundles];
    }

    removeScripts(scripts, wpData);

    if (data?.x_metadata?.vcvSourceCssFileUrl) {
      removeCss(data.x_metadata.vcvSourceCssFileUrl);
    }
  };

  connectCss = path => {
    const head = document.getElementsByTagName('head')[0];
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = path;

    head.appendChild(link);

    link.onload = () => this.setState({ mainCssLoaded: true }, () => runComposer(wpData));
    link.onerror = () => this.setState({ mainCssLoaded: true }, () => runComposer(wpData));
  };

  render() {
    const { mainCssLoaded } = this.state;
    const { t, isAuth, data, loading } = this.props;
    const hasData = !!Object.keys(data).length;
    const showContent = !loading && data?.content?.rendered && mainCssLoaded;

    const seoData = {
      canonicalUrl: `${siteUrl}${ROUTE_TERMS_AND_CONDITIONS}`,
      ogUrl: `${siteUrl}${ROUTE_TERMS_AND_CONDITIONS}`,
      title: 'Terms and Conditions',
    };

    return (
      <Layout>
        {!loading && hasData && (
          <Helmet onChangeClientState={this.handleChangeHelmetData}>
            <title>{seoData.title}</title>
            <link
              rel="stylesheet"
              href={`${wpComposerPublicPath}/dist/front.bundle.css`}
              media="all"
            />
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            {data?.x_metadata?.vcvSourceAssetsFiles?.cssBundles &&
              this.getCss(data.x_metadata.vcvSourceAssetsFiles.cssBundles)}
            {data?.x_featured_media_medium && (
              <meta name="twitter:image" content={data.x_featured_media_medium} />
            )}
            <meta name="twitter:title" content={seoData.title} />
            {data?.excerpt?.rendered && (
              <meta
                name="twitter:description"
                content={`MyBundle - ${removeHTMLEntity(data.excerpt.rendered)}`}
              />
            )}
            <meta name="twitter:site" content="@mybundletv" />
            <meta name="twitter:creator" content="@mybundletv" />
            <meta name="twitter:card" content="summary" />
            {data?.yoast_meta && this.getMeta(data.yoast_meta)}
          </Helmet>
        )}
        <div className="page-content static-pages">
          <RouterBreadcrumbs />

          <div className="static-pages__wrapper">
            <div className="title-container">
              <Typography align="center" variant="h2">
                {t('termsAndConditions.title')}
              </Typography>
              {hasData && (
                <Typography align="center" variant="h6">
                  {t('termsAndConditions.subtitle', {
                    date: formatDate(new Date(data.modified).toString(), 'MMMM d, yyyy'),
                  })}
                </Typography>
              )}
            </div>

            <div className="static-pages__content">
              <WPContent className={clsx(data.slug, 'post-page', isAuth && 'auth')}>
                {showContent && (
                  <ReactHtml className="markdown">{data.content.rendered.trim()}</ReactHtml>
                )}
              </WPContent>
              <Loader
                fullFilled
                active={loading || !mainCssLoaded}
                text={t('termsAndConditions.loaders.default')}
              />
            </div>
          </div>
        </div>
      </Layout>
    );
  }
}

TermsAndConditions.propTypes = {
  t: PropTypes.func.isRequired,
};

const mapStateToProps = ({ wpBlog, partner, auth }) => ({
  analyticsLocation: LOCATIONS_TERMS_CONDITIONS_PAGE,
  isAuth: auth.isAuth,
  data: wpBlog.pageData,
  loading: wpBlog.loadings.main,
  partner: partner.data,
});

const mapDispatchToProps = dispatch => {
  return {
    getWPPage: slug => dispatch(WPBlogActions.getWPPage.action({ slug })),
    manageAuthDialogState: props =>
      dispatch(AuthActions.manageAuthDialogState.action({ state: true, props })),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(),
)(TermsAndConditions);
